import React, { useEffect, useState, useContext } from 'react';
import { X, Loader2, ChevronDown, ArrowLeft } from 'lucide-react';
import { authService } from '../../services/auth';
import { formatBrazilianPhoneNumber } from '../../utils/fieldCache';
import { ToastContext } from '../../App';
const RegisterModalNoCode = ({
  isOpen,
  onClose,
  onLoginClick,
  pendingAnalysis = false
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    confirmed_email: '',
    password: '',
    passwordConfirmation: '',
    phone: '',
    a_empresa_em_que_trabalho_e___: '',
    seu_cargo: '',
    numero_de_funcionarios_na_empresa_em_que_trabalho: '',
    area_de_atuacao: '',
    website: '',
    como_voce_ouviu_falar_sobre_a_conversion_: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const {
    addToast
  } = useContext(ToastContext);
  useEffect(() => {
    if (isOpen) {
      // Reset form state when modal is opened
      setStep(1);
      setError('');
      setIsSuccess(false);
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        confirmed_email: '',
        password: '',
        passwordConfirmation: '',
        phone: '',
        a_empresa_em_que_trabalho_e___: '',
        seu_cargo: '',
        numero_de_funcionarios_na_empresa_em_que_trabalho: '',
        area_de_atuacao: '',
        website: '',
        como_voce_ouviu_falar_sobre_a_conversion_: ''
      });
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    if (name === 'phone') {
      // Format phone number as user types
      setFormData(prev => ({
        ...prev,
        [name]: formatBrazilianPhoneNumber(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  const validateStep1 = () => {
    if (!formData.firstname.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.lastname.trim()) {
      setError('Sobrenome é obrigatório');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email é obrigatório');
      return false;
    }
    if (!formData.confirmed_email.trim()) {
      setError('Confirmação de email é obrigatória');
      return false;
    }
    if (formData.email !== formData.confirmed_email) {
      setError('Os emails não conferem');
      return false;
    }
    if (!formData.password.trim()) {
      setError('Senha é obrigatória');
      return false;
    }
    // Validação de senha completa
    if (formData.password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      return false;
    }
    if (!/[A-Za-z]/.test(formData.password)) {
      setError('Senha deve conter pelo menos uma letra');
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      setError('Senha deve conter pelo menos um número');
      return false;
    }
    if (!/[^A-Za-z0-9]/.test(formData.password)) {
      setError('Senha deve conter pelo menos um símbolo');
      return false;
    }
    if (!formData.passwordConfirmation.trim()) {
      setError('Confirmação de senha é obrigatória');
      return false;
    }
    if (formData.password !== formData.passwordConfirmation) {
      setError('As senhas não conferem');
      return false;
    }
    return true;
  };
  const validateStep2 = () => {
    if (!formData.phone.trim()) {
      setError('Telefone é obrigatório');
      return false;
    }
    if (!formData.a_empresa_em_que_trabalho_e___) {
      setError('Segmento é obrigatório');
      return false;
    }
    if (!formData.seu_cargo) {
      setError('Cargo é obrigatório');
      return false;
    }
    if (!formData.numero_de_funcionarios_na_empresa_em_que_trabalho) {
      setError('Número de funcionários é obrigatório');
      return false;
    }
    if (!formData.area_de_atuacao) {
      setError('Área de atuação é obrigatória');
      return false;
    }
    if (!formData.website.trim()) {
      setError('URL do site é obrigatória');
      return false;
    }
    return true;
  };
  const handleNextStep = async () => {
    // Validar campos básicos
    if (!validateStep1()) {
      return;
    }
    // Avançar para o próximo passo (sem verificação de código)
    setError('');
    setStep(2);
  };
  const handlePrevStep = () => {
    setStep(1);
    setError('');
  };
  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateStep2()) {
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      // Prepare data for API
      const callbackUrl = `${window.location.origin}/verificar-email?token={{token}}`;
      // Format phone number - remove non-digits
      const phoneDigits = formData.phone.replace(/\D/g, '');
      // Ensure URL has protocol
      let website = formData.website.trim();
      if (website && !website.match(/^https?:\/\//)) {
        website = 'https://' + website;
      }
      // Map form fields to backend expected fields - NO invite_code
      const dataToSubmit = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: phoneDigits.startsWith('+55') ? phoneDigits : `+55${phoneDigits}`,
        website: website,
        como_voce_ouviu_falar_sobre_a_conversion_: formData.como_voce_ouviu_falar_sobre_a_conversion_ || '',
        a_empresa_em_que_trabalho_e___: formData.a_empresa_em_que_trabalho_e___,
        seu_cargo: formData.seu_cargo,
        numero_de_funcionarios_na_empresa_em_que_trabalho: formData.numero_de_funcionarios_na_empresa_em_que_trabalho,
        area_de_atuacao: formData.area_de_atuacao,
        password: formData.password,
        callbackUrl
      };
      // Register the user (without invite code)
      await authService.register(dataToSubmit);
      // Prepare data for CRM registration
      const crmData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: phoneDigits.startsWith('+55') ? phoneDigits : `+55${phoneDigits}`,
        website: website,
        como_voce_ouviu_falar_sobre_a_conversion_: formData.como_voce_ouviu_falar_sobre_a_conversion_ || '',
        a_empresa_em_que_trabalho_e___: formData.a_empresa_em_que_trabalho_e___,
        seu_cargo: formData.seu_cargo,
        numero_de_funcionarios_na_empresa_em_que_trabalho: formData.numero_de_funcionarios_na_empresa_em_que_trabalho,
        area_de_atuacao: formData.area_de_atuacao
      };
      // Send data to CRM (don't await this call to not block the user flow)
      authService.registerCRM(crmData);
      setIsLoading(false);
      setIsSuccess(true);
      addToast('success', 'Conta criada com sucesso! Verifique seu email para ativar.');
    } catch (error) {
      setIsLoading(false);
      // Handle validation errors
      if (error.response?.data?.details) {
        const details = error.response.data.details;
        const errorMessage = details.map(d => d.message).join(', ');
        setError(`Erro de validação: ${errorMessage}`);
      } else {
        setError(error.response?.data?.message || 'Erro ao criar conta. Verifique os dados e tente novamente.');
      }
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto" data-id="element-360" data-mp="3p6hll7ibgku4">
      <div className="relative w-full max-w-3xl p-6 mx-auto bg-white rounded-lg shadow-xl flex flex-col my-8" data-id="element-361" data-mp="3faectisylny7">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700" aria-label="Close" disabled={isLoading} data-id="element-362" data-mp="3b9ufae4h4ibj">
          <X size={20} data-id="element-363" data-mp="1zte6h3nfczxn" />
        </button>

        {isSuccess ? <div className="py-8 text-center flex-grow flex flex-col items-center justify-center" data-id="element-364" data-mp="1zwa6cxiu1yoq">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center" data-id="element-365" data-mp="2vmgo71rvovx">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-366" data-mp="3pxzcrbus0kma">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-367" data-mp="mibr0m20hbrj" />
              </svg>
            </div>
            <h2 className="mb-2 text-2xl font-bold text-gray-900" data-id="element-368" data-mp="cr7dlqrzhyyt">
              Conta criada com sucesso!
            </h2>
            <p className="mb-6 text-gray-600" data-id="element-369" data-mp="15g4txocmxlox">
              Enviamos um email de confirmação para{' '}
              <strong data-id="element-370" data-mp="1a6uyizd8lkik">{formData.email}</strong>. Por favor, verifique sua caixa
              de entrada e clique no link para ativar sua conta.
            </p>
            <button onClick={onClose} className="px-6 py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-371" data-mp="1jk3t592af5tg">
              Fechar
            </button>
          </div> : <div className="flex flex-col" data-id="element-372" data-mp="1ocwwptm0s83y">
            {step === 2 && <div className="flex items-center mb-4" data-id="element-373" data-mp="10nu5pv5hx2ws">
                <button onClick={handlePrevStep} className="mr-2 text-gray-500 hover:text-gray-700" disabled={isLoading} data-id="element-374" data-mp="10olzr1jg2sky">
                  <ArrowLeft size={20} data-id="element-375" data-mp="1aim4h2atdvt5" />
                </button>
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-376" data-mp="15l40lqbnm6s0">
                  Só faltam estes dados
                </h2>
              </div>}
            {step === 1 && <h2 className="mb-4 text-2xl font-bold text-center text-gray-900" data-id="element-377" data-mp="2h098d8qn6vih">
                Precisamos de alguns dados seus
              </h2>}

            {step === 2 && <div className="w-full mb-6 bg-gray-200 rounded-full h-2.5" data-id="element-378" data-mp="olloq2genjvm">
                <div className="bg-[#1649ff] h-2.5 rounded-full w-3/4" data-id="element-379" data-mp="jtc14t2rivwi"></div>
              </div>}

            {pendingAnalysis && <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded-md" data-id="element-380" data-mp="eu7qdy06wq42">
                Crie uma conta para continuar com a análise da sua marca.
              </div>}

            {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md" data-id="element-381" data-mp="5w5lh7dc5dbi">
                {error}
              </div>}

            {step === 1 ? <form data-id="element-382" data-mp="3s2c8dk4wtvfs">
                <div className="space-y-4" data-id="element-383" data-mp="3s2u3uejr05mn">
                  <div className="grid grid-cols-2 gap-4" data-id="element-384" data-mp="1v8qyhxyvvps5">
                    <div data-id="element-385" data-mp="1v87p55nau9pq">
                      <label htmlFor="register-firstname" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-386" data-mp="1q9wg2x4tw38k">
                        Nome *
                      </label>
                      <input type="text" id="register-firstname" name="firstname" value={formData.firstname} onChange={handleChange} placeholder="Seu nome" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="given-name" data-id="element-387" data-mp="22r79bdil45a5" />
                    </div>
                    <div data-id="element-388" data-mp="1tpqw6y0dxp28">
                      <label htmlFor="register-lastname" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-389" data-mp="3085yso53x2m2">
                        Sobrenome *
                      </label>
                      <input type="text" id="register-lastname" name="lastname" value={formData.lastname} onChange={handleChange} placeholder="Seu sobrenome" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="family-name" data-id="element-390" data-mp="2fha6r0qsrnth" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-391" data-mp="21qwysydwo945">
                    <div data-id="element-392" data-mp="21plwz440tbfo">
                      <label htmlFor="register-email" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-393" data-mp="1e1cas12walo0">
                        E-mail corporativo *
                      </label>
                      <input type="email" id="register-email" name="email" value={formData.email} onChange={handleChange} placeholder="exemplo@empresa.com.br" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="email" data-id="element-394" data-mp="3ko1mxz9emejw" />
                    </div>
                    <div data-id="element-395" data-mp="2bvdwpv9kv9o">
                      <label htmlFor="register-confirmed-email" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-396" data-mp="2b3uedexynzr">
                        Confirmar e-mail *
                      </label>
                      <input type="email" id="register-confirmed-email" name="confirmed_email" value={formData.confirmed_email} onChange={handleChange} placeholder="exemplo@empresa.com.br" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="email" data-id="element-397" data-mp="3398shmkl6hr7" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-398" data-mp="3cc1mvyp2f9l9">
                    <div data-id="element-399" data-mp="388byb98e4s08">
                      <label htmlFor="register-password" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-400" data-mp="38ay87fir7jlm">
                        Senha *
                      </label>
                      <input type="password" id="register-password" name="password" value={formData.password} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} minLength={8} autoComplete="new-password" data-id="element-401" data-mp="ccfjrdqg044k" />
                      <p className="mt-1 text-xs text-gray-500" data-id="element-402" data-mp="143mwlor6b1nt">
                        Mínimo de 8 caracteres, incluindo pelo menos 1 letra, 1
                        número e 1 símbolo
                      </p>
                    </div>
                    <div data-id="element-403" data-mp="35w1bsg2c1s0j">
                      <label htmlFor="register-passwordConfirmation" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-404" data-mp="3asrrh2a86llf">
                        Confirmar senha *
                      </label>
                      <input type="password" id="register-passwordConfirmation" name="passwordConfirmation" value={formData.passwordConfirmation} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} minLength={8} autoComplete="new-password" data-id="element-405" data-mp="d1j8xhd3cb9c" />
                    </div>
                  </div>
                </div>

                <div className="mt-6" data-id="element-406" data-mp="1jlk91zzc4t7t">
                  <button type="button" onClick={handleNextStep} className="w-full py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isLoading} data-id="element-407" data-mp="1eplhy4mmgx3p">
                    Continuar
                  </button>
                  <div className="mt-4 text-center" data-id="element-408" data-mp="1t9xx5mnn8ntv">
                    <p className="text-sm text-gray-600" data-id="element-409" data-mp="1fkdzuwcu0iry">
                      Já tem uma conta?{' '}
                      <button onClick={onLoginClick} className="text-[#1649ff] hover:underline" disabled={isLoading} type="button" data-id="element-410" data-mp="1kbck270xy10h">
                        Entrar
                      </button>
                    </p>
                  </div>
                </div>
              </form> : <form onSubmit={handleSubmit} data-id="element-411" data-mp="10lsc0ysogwti">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-412" data-mp="10oy5eq8cakv1">
                  <div className="md:col-span-1" data-id="element-413" data-mp="1h1uhx2j9p4h5">
                    <label htmlFor="register-phone" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-414" data-mp="1c3j8uu0sqxzz">
                      Número de telefone *
                    </label>
                    <input type="tel" id="register-phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="(XX) XXXXX-XXXX" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="tel" data-id="element-415" data-mp="124k8rzu3kaol" />
                  </div>
                  <div className="md:col-span-1" data-id="element-416" data-mp="af7amle1o90z">
                    <label htmlFor="register-website" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-417" data-mp="2m19i7sphqhb2">
                      URL do site *
                    </label>
                    <input type="text" id="register-website" name="website" value={formData.website} onChange={handleChange} placeholder="www.exemplo.com.br" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="url" data-id="element-418" data-mp="2v2z6ipo2t04m" />
                  </div>

                  <div data-id="element-419" data-mp="1jlzi454un5fv">
                    <label htmlFor="register-segment" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-420" data-mp="1jmisn0s66rjv">
                      Segmento *
                    </label>
                    <div className="relative" data-id="element-421" data-mp="11i9a2sjtcp67">
                      <select id="register-segment" name="a_empresa_em_que_trabalho_e___" value={formData.a_empresa_em_que_trabalho_e___} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} data-id="element-422" data-mp="11gy6asqdbhf3">
                        <option value="" disabled data-id="element-423" data-mp="np9xozfbvb2v">
                          Selecione o segmento
                        </option>
                        <option value="Agência / Consultoria" data-id="element-424" data-mp="3r8mo4eiwfy47">
                          Agência / Consultoria
                        </option>
                        <option value="Agronegócio" data-id="element-425" data-mp="3w53dfc1u4iiv">Agronegócio</option>
                        <option value="Autoconhecimento" data-id="element-426" data-mp="2das0idmh306h">
                          Autoconhecimento
                        </option>
                        <option value="Automotiva" data-id="element-427" data-mp="2983su99hvzb3">Automotiva</option>
                        <option value="Bens de Consumo / Alimentação" data-id="element-428" data-mp="249tvpt47momu">
                          Bens de Consumo / Alimentação
                        </option>
                        <option value="E-commerce" data-id="element-429" data-mp="1ucpabvi6ux73">E-commerce</option>
                        <option value="Educação" data-id="element-430" data-mp="1uf20ux20ifvr">Educação</option>
                        <option value="Empresa pública / ONGs" data-id="element-431" data-mp="1plfl2w0jxvvq">
                          Empresa pública / ONGs
                        </option>
                        <option value="Energia" data-id="element-432" data-mp="1glj2nc4r9t94">Energia</option>
                        <option value="Eventos" data-id="element-433" data-mp="1gn3zj8ejloa5">Eventos</option>
                        <option value="Financeiro" data-id="element-434" data-mp="1bmpnlf9tlu6u">Financeiro</option>
                        <option value="Imobiliário" data-id="element-435" data-mp="16nd2ayylkv6d">Imobiliário</option>
                        <option value="Indústria" data-id="element-436" data-mp="16o4x47kdeovl">Indústria</option>
                        <option value="Infoprodutos" data-id="element-437" data-mp="wypm9j5oojbz">Infoprodutos</option>
                        <option value="Mídia comunicação" data-id="element-438" data-mp="11ykafey2qutc">
                          Mídia comunicação
                        </option>
                        <option value="Mobilidade" data-id="element-439" data-mp="o0dxlmodnvm6">Mobilidade</option>
                        <option value="Moda / Vestuário" data-id="element-440" data-mp="s04m1jqflznj">
                          Moda / Vestuário
                        </option>
                        <option value="Religião / Espiritualidade" data-id="element-441" data-mp="1fqs7z8x6mpt3">
                          Religião / Espiritualidade
                        </option>
                        <option value="Saúde / Beleza / Bem-Estar" data-id="element-442" data-mp="16y6ofqlq8f37">
                          Saúde / Beleza / Bem-Estar
                        </option>
                        <option value="Serviços gerais" data-id="element-443" data-mp="1axpbot10t297">Serviços gerais</option>
                        <option value="Tecnologia / SaaS" data-id="element-444" data-mp="1yob81n1nwy3d">
                          Tecnologia / SaaS
                        </option>
                        <option value="Telecomunicações" data-id="element-445" data-mp="23hxj5as6gtx2">
                          Telecomunicações
                        </option>
                        <option value="Transporte / Logística" data-id="element-446" data-mp="3j7otejpxkd0a">
                          Transporte / Logística
                        </option>
                        <option value="Turismo / Lazer" data-id="element-447" data-mp="1pvt54t79jcl">Turismo / Lazer</option>
                        <option value="Varejo" data-id="element-448" data-mp="3sy9o3w2hgxdx">Varejo</option>
                        <option value="Outro" data-id="element-449" data-mp="357473dkt3z21">Outro</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-450" data-mp="308avjqp2yi4i" />
                    </div>
                  </div>

                  <div data-id="element-451" data-mp="3a5nvdkmvhmxk">
                    <label htmlFor="register-cargo" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-452" data-mp="3a55qy0q242ek">
                      Seu cargo *
                    </label>
                    <div className="relative" data-id="element-453" data-mp="2v4s5z53hahjm">
                      <select id="register-cargo" name="seu_cargo" value={formData.seu_cargo} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} autoComplete="organization-title" data-id="element-454" data-mp="2v639r4wxbpaq">
                        <option value="" disabled data-id="element-455" data-mp="3ntfvjcv9ft2z">
                          Selecione seu cargo
                        </option>
                        <option value="CEO" data-id="element-456" data-mp="3rtpzyl3embfq">CEO</option>
                        <option value="Fundador" data-id="element-457" data-mp="3rwm1skidhk9g">Fundador</option>
                        <option value="Presidente" data-id="element-458" data-mp="q1enbvfuwhp">Presidente</option>
                        <option value="Diretor" data-id="element-459" data-mp="5mrtxwzf5o22">Diretor</option>
                        <option value="Gerente" data-id="element-460" data-mp="frhxg7ah108e">Gerente</option>
                        <option value="Coordenador" data-id="element-461" data-mp="ft2rzwwscj69">Coordenador</option>
                        <option value="Analista" data-id="element-462" data-mp="as58nh01hj36">Analista</option>
                        <option value="Consultor" data-id="element-463" data-mp="5tbzg0rsce8t">Consultor</option>
                        <option value="Autônomo" data-id="element-464" data-mp="5tlnhit9sbbv">Autônomo</option>
                        <option value="Supervisor" data-id="element-465" data-mp="1ss1iuze16lm">Supervisor</option>
                        <option value="Assessor" data-id="element-466" data-mp="yhpxggivjakd">Assessor</option>
                        <option value="Assistente" data-id="element-467" data-mp="yk3pegi9c6np">Assistente</option>
                        <option value="Estagiário" data-id="element-468" data-mp="tk7k8dxtq96k">Estagiário</option>
                        <option value="Estudante" data-id="element-469" data-mp="oro6ngz4ze1r">Estudante</option>
                        <option value="Professor" data-id="element-470" data-mp="optlc1jydjx5">Professor</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-471" data-mp="korvucfu8m8d" />
                    </div>
                  </div>

                  <div data-id="element-472" data-mp="36pcmaz0hmqjm">
                    <label htmlFor="register-funcionarios" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-473" data-mp="36q4iaaxzyqaf">
                      Número de funcionários *
                    </label>
                    <div className="relative" data-id="element-474" data-mp="31ymdtcwrm7kp">
                      <select id="register-funcionarios" name="numero_de_funcionarios_na_empresa_em_que_trabalho" value={formData.numero_de_funcionarios_na_empresa_em_que_trabalho} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} data-id="element-475" data-mp="31xba1d3bkztl">
                        <option value="" disabled data-id="element-476" data-mp="h6auxa8dqoej">
                          Selecione a quantidade
                        </option>
                        <option value="1 - 10" data-id="element-477" data-mp="1ws4oe4711s11">1 - 10</option>
                        <option value="11 - 20" data-id="element-478" data-mp="1wpryn4v12ddf">11 - 20</option>
                        <option value="21 - 50" data-id="element-479" data-mp="2bhanls5v7i8x">21 - 50</option>
                        <option value="51 - 100" data-id="element-480" data-mp="26pai7k70zx4y">51 - 100</option>
                        <option value="101 - 200" data-id="element-481" data-mp="26npq0186oqa9">101 - 200</option>
                        <option value="201 - 500" data-id="element-482" data-mp="2kfw5rm7fcv96">201 - 500</option>
                        <option value="501 - 1.000" data-id="element-483" data-mp="2gg55mrw0d2rz">501 - 1.000</option>
                        <option value="1.001 - 5.000" data-id="element-484" data-mp="2ufd6rqbqekyt">1.001 - 5.000</option>
                        <option value="Acima de 5.000" data-id="element-485" data-mp="2uelkj640xzl7">Acima de 5.000</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-486" data-mp="2pe7a5heyadk0" />
                    </div>
                  </div>

                  <div data-id="element-487" data-mp="99vbcdjv7jdw">
                    <label htmlFor="register-area" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-488" data-mp="98bv2my7l8c4">
                      Área de atuação *
                    </label>
                    <div className="relative" data-id="element-489" data-mp="nxzwrpaay90d">
                      <select id="register-area" name="area_de_atuacao" value={formData.area_de_atuacao} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} data-id="element-490" data-mp="nwyb6qvnu44e">
                        <option value="" disabled data-id="element-491" data-mp="1bmivpe7gp9fs">
                          Selecione a área
                        </option>
                        <option value="Administrativo" data-id="element-492" data-mp="1gku4rmpxnfwy">Administrativo</option>
                        <option value="Comercial" data-id="element-493" data-mp="313eeb40dxf4v">Comercial</option>
                        <option value="Contábil" data-id="element-494" data-mp="2w5342s66h2m4">Contábil</option>
                        <option value="Customer Success" data-id="element-495" data-mp="2w7z0gc2dnj8g">
                          Customer Success
                        </option>
                        <option value="E-commerce" data-id="element-496" data-mp="2mgbgew85h4h0">E-commerce</option>
                        <option value="Financeiro" data-id="element-497" data-mp="2mdyxogukiq2w">Financeiro</option>
                        <option value="Marketing" data-id="element-498" data-mp="2hi9u82ffgp1b">Marketing</option>
                        <option value="Operação" data-id="element-499" data-mp="2devdtanku6nc">Operação</option>
                        <option value="RH" data-id="element-500" data-mp="28g0p5s2xhnvb">RH</option>
                        <option value="SEO" data-id="element-501" data-mp="28gtpaa7dtf4h">SEO</option>
                        <option value="Tecnologia" data-id="element-502" data-mp="23h79bt065krz">Tecnologia</option>
                        <option value="Outra área" data-id="element-503" data-mp="23j1xrhanfv0t">Outra área</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-504" data-mp="6mktggrzhith" />
                    </div>
                  </div>

                  <div className="md:col-span-2" data-id="element-505" data-mp="n16o4u44lp4q">
                    <label htmlFor="register-como-conheceu" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-506" data-mp="myao908pwqdn">
                      Como você ouviu falar sobre a Conversion?
                    </label>
                    <input type="text" id="register-como-conheceu" name="como_voce_ouviu_falar_sobre_a_conversion_" value={formData.como_voce_ouviu_falar_sobre_a_conversion_} onChange={handleChange} placeholder="Google, indicação, redes sociais, etc." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" disabled={isLoading} data-id="element-507" data-mp="915mvdjy8foq" />
                  </div>
                </div>

                <div className="mt-6 flex space-x-4" data-id="element-508" data-mp="21xhl8sqvxznd">
                  <button type="button" onClick={handlePrevStep} className="w-1/3 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2" disabled={isLoading} data-id="element-509" data-mp="21z2hqnwrfsnv">
                    Voltar
                  </button>
                  <button type="submit" className="w-2/3 py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isLoading} data-id="element-510" data-mp="2kmo0g5sh094f">
                    {isLoading ? <>
                        <Loader2 size={18} className="mr-2 animate-spin" data-id="element-511" data-mp="33fif0ym6lz84" />
                        Processando...
                      </> : 'Cadastre-se'}
                  </button>
                </div>
              </form>}
          </div>}
      </div>
    </div>;
};
export default RegisterModalNoCode;